import React, { useState } from 'react';
import { X, AlertCircle, Copy } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'react-toastify';

const VideoPreviewModal = ({ video, isOpen, onClose }) => {
  const [videoError, setVideoError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  if (!isOpen || !video) return null;

  const handleCopyCode = (code) => {
    // Universal copy method that works in all environments
    const copyToClipboard = (text) => {
      // Create temporary textarea
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "absolute";
      textArea.style.opacity = "0";
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      
      try {
        textArea.select();
        textArea.setSelectionRange(0, 99999); // For mobile devices
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          toast.success(`✓ Code ${code} copied!`, {
            position: "bottom-center",
            autoClose: 2000,
            hideProgressBar: true,
            closeButton: false,
            style: {
              background: '#10b981',
              color: 'white',
              borderRadius: '9999px',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: '600',
            }
          });
        } else {
          throw new Error('Copy command failed');
        }
      } catch (err) {
        document.body.removeChild(textArea);
        console.error('Copy failed:', err);
        toast.error(`Failed to copy. Code: ${code}`, {
          position: "bottom-center",
          autoClose: 4000,
        });
      }
    };

    copyToClipboard(code);
  };

  const hasPreview = video.videoPreview && video.videoPreview.trim() !== '';
  
  // Extract video URL - handle different formats
  const extractUrl = (urlString) => {
    if (!urlString) return null;
    
    // If it's already a clean URL, return it
    if (urlString.startsWith('http://') || urlString.startsWith('https://')) {
      return urlString;
    }
    
    // Extract from format "filename (url)"
    const match = urlString.match(/\((https?:\/\/[^\)]+)\)/);
    return match ? match[1] : urlString;
  };

  const previewUrl = extractUrl(video.videoPreview);

  const handleVideoError = (e) => {
    console.error('Video load error:', e);
    console.error('Failed URL:', previewUrl);
    setVideoError(true);
    
    // Determine error message based on URL
    if (previewUrl.includes('pimpbunny.com')) {
      setErrorMessage('This video host requires authentication or has CORS restrictions. Please use a direct video link from a public CDN (e.g., AWS S3, Cloudflare, or public file hosting).');
    } else {
      setErrorMessage('Unable to load video. Please ensure the URL is a direct link to a video file (.mp4, .webm, .mov) and is publicly accessible.');
    }
  };

  const handleClose = () => {
    setVideoError(false);
    setErrorMessage('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-2 md:p-4" onClick={handleClose}>
      <div className="relative w-full max-w-4xl bg-white rounded-lg overflow-hidden max-h-[95vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button
          onClick={handleClose}
          aria-label="Close video preview"
          className="absolute top-2 right-2 md:top-4 md:right-4 z-10 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full p-2 transition-all"
        >
          <X className="w-5 h-5 md:w-6 md:h-6" />
        </button>

        {/* Video Player */}
        {hasPreview && previewUrl ? (
          <div className="aspect-video bg-black">
            {videoError ? (
              <div className="w-full h-full flex items-center justify-center p-4 md:p-8">
                <div className="text-center">
                  <AlertCircle className="w-12 h-12 md:w-16 md:h-16 text-red-500 mx-auto mb-4" />
                  <h3 className="text-white text-lg md:text-xl font-bold">Video Preview Unavailable</h3>
                </div>
              </div>
            ) : (
              <video
                key={previewUrl}
                controls
                autoPlay
                preload="none"
                className="w-full h-full"
                onError={handleVideoError}
                onLoadStart={() => { setVideoError(false); }}
              >
                <source src={previewUrl} type="video/mp4" />
                <source src={previewUrl} type="video/webm" />
                <source src={previewUrl} type="video/mov" />
                <track kind="captions" src="/captions/empty.vtt" srcLang="fr" label="Français" default />
                Your browser does not support the video tag.
              </video>
            )}
          </div>
        ) : (
          <div className="aspect-video bg-gray-900 flex items-center justify-center">
            <div className="text-center p-4 md:p-8">
              <p className="text-white text-base md:text-lg mb-2">No preview available</p>
              <p className="text-gray-400 text-xs md:text-sm">Add a video preview URL in the admin panel to enable preview playback</p>
            </div>
          </div>
        )}

        {/* Video Info */}
        <div className="p-4 md:p-6">
          <h3 className="text-xl md:text-2xl font-bold mb-2">{video.title}</h3>
          <p className="text-sm md:text-base text-gray-600 mb-4">{video.description}</p>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3 md:gap-4 text-xs md:text-sm text-gray-600">
              <span>Duration: {video.duration}</span>
              <div className="flex items-center gap-2">
                <span>Code: {video.videoCode}</span>
                <button
                  onClick={() => handleCopyCode(video.videoCode)}
                  className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                  title="Copy code"
                  aria-label="Copy video code"
                >
                  <Copy className="w-4 h-4 text-gray-600" />
                </button>
              </div>
            </div>
            <Button
              className="bg-sky-400 hover:bg-sky-500 text-white w-full md:w-auto text-sm md:text-base py-5 md:py-6"
              onClick={() => window.open('https://onlyfans.com/my/chats/chat/960542/', '_blank')}
            >
              Get Full Video
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPreviewModal;
